import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DEFAULT_AGENT_MODEL } from '@troia-v3/types';

import { Drawer } from '@/shared/components/ui/Drawer';
import { ChatPanel } from './ChatPanel';
import { PreviewPanel } from './PreviewPanel';
import { PromptMigrationWizard } from '../migration/PromptMigrationWizard';
import { useChatDraft } from '../../hooks/useAgentDraft';
import { useCreateAgent } from '../../hooks/useCreateAgent';
import type { ActionIntent, AgentDraft, ChatMessage, MessageComponent } from '../../services/agentDraftApi';

// ─── Types ──────────────────────────────────────────

interface UIMessage {
  id: string;
  role: 'assistant' | 'user';
  content: string;
}

interface AgentCreationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type Phase = 'chatting' | 'migrating' | 'creating';

// ─── Initial greeting ───────────────────────────────

const INITIAL_GREETING = 'Olá! Vou te ajudar a criar seu agente de IA.\nMe conta: o que seu agente precisa fazer?';

// ─── Component ──────────────────────────────────────

export function AgentCreationDrawer({ isOpen, onClose }: AgentCreationDrawerProps) {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('chatting');
  const [messages, setMessages] = useState<UIMessage[]>([
    { id: 'init', role: 'assistant', content: INITIAL_GREETING },
  ]);
  const [draft, setDraft] = useState<AgentDraft | null>(null);
  const [pendingComponent, setPendingComponent] = useState<MessageComponent | undefined>(undefined);

  const chatDraft = useChatDraft();
  const createAgent = useCreateAgent();

  // ─── Helpers ────────────────────────────────────

  const msgCounter = useRef(0);
  const makeMsg = (role: 'assistant' | 'user', content: string): UIMessage => ({
    id: `msg-${++msgCounter.current}`,
    role,
    content,
  });

  // ─── Handlers ───────────────────────────────────

  const makeDefaultDraft = useCallback(
    (): AgentDraft => ({
      name: '',
      description: '',
      systemPrompt: '',
      category: 'other',
      segment: 'generic',
      model: DEFAULT_AGENT_MODEL,
      temperature: 0.5,
      enabledCapabilities: [],
      suggestedDatabases: [],
      migrationBlocks: [],
    }),
    [],
  );

  const handleActionConfirm = useCallback(
    ({ displayMessage, intent }: { displayMessage: string; intent: ActionIntent }) => {
      setPendingComponent(undefined);

      const userMsg: ChatMessage = { role: 'user', content: displayMessage };
      const updatedApiMessages: ChatMessage[] = [
        ...messages.map((m) => ({ role: m.role, content: m.content })),
        userMsg,
      ];

      setMessages((prev) => [...prev, makeMsg('user', displayMessage)]);

      const currentDraft: AgentDraft = draft ?? makeDefaultDraft();

      chatDraft.mutate(
        { messages: updatedApiMessages, currentDraft, pendingIntent: intent },
        {
          onSuccess: (data) => {
            setDraft(data.draft);
            setMessages((prev) => [...prev, makeMsg('assistant', data.message)]);
            setPendingComponent(data.messageComponent);
          },
          onError: () => {
            setMessages((prev) => [
              ...prev,
              makeMsg('assistant', 'Desculpe, ocorreu um erro. Tente novamente.'),
            ]);
          },
        },
      );
    },
    [draft, messages, chatDraft, makeDefaultDraft],
  );

  const handleSendMessage = useCallback(
    (userMessage: string) => {
      // Limpar componente pendente
      setPendingComponent(undefined);

      const userMsg: ChatMessage = { role: 'user', content: userMessage };
      const updatedApiMessages: ChatMessage[] = [
        ...messages.map((m) => ({ role: m.role, content: m.content })),
        userMsg,
      ];

      // Add user message to UI immediately
      setMessages((prev) => [...prev, makeMsg('user', userMessage)]);

      const currentDraft: AgentDraft = draft ?? makeDefaultDraft();

      chatDraft.mutate(
        { messages: updatedApiMessages, currentDraft },
        {
          onSuccess: (data) => {
            setDraft(data.draft);
            setMessages((prev) => [...prev, makeMsg('assistant', data.message)]);
            setPendingComponent(data.messageComponent);
          },
          onError: () => {
            setMessages((prev) => [
              ...prev,
              makeMsg('assistant', 'Desculpe, ocorreu um erro. Pode tentar novamente?'),
            ]);
          },
        },
      );
    },
    [draft, messages, chatDraft, makeDefaultDraft],
  );

  const buildCreatePayload = useCallback(
    (currentDraft: AgentDraft, overridePrompt?: string) => {
      // Banco de dados: prioriza databaseIds (novos) e cai para suggestedDatabases (legado)
      const databases: string[] =
        currentDraft.databaseIds && currentDraft.databaseIds.length > 0
          ? currentDraft.databaseIds
          : currentDraft.suggestedDatabases
              .filter((db) => db.existingDatabaseId)
              .map((db) => db.existingDatabaseId!);

      return {
        name: currentDraft.name,
        description: currentDraft.description,
        systemPrompt: overridePrompt ?? currentDraft.systemPrompt,
        // TEMPORÁRIO (23/10/2026): só o GPT-6 Luna está liberado, então o
        // modelo que a IA do assistente sugeriu é ignorado. Para voltar:
        // model: currentDraft.model,
        model: DEFAULT_AGENT_MODEL,
        temperature: currentDraft.temperature,
        category: currentDraft.category as Parameters<typeof createAgent.mutate>[0]['category'],
        segment: currentDraft.segment as Parameters<typeof createAgent.mutate>[0]['segment'],
        enabledCapabilities: currentDraft.enabledCapabilities.map((cap) => ({
          capability: cap as string & Parameters<typeof createAgent.mutate>[0]['enabledCapabilities'] extends
            | Array<{ capability: infer C }>
            | undefined
            ? C
            : never,
          enabled: true,
          config: currentDraft.capabilityConfigs
            ? (currentDraft.capabilityConfigs as Record<string, Record<string, unknown>>)[cap]
            : undefined,
        })),
        databases,
        triggers: { message_received: { enabled: true } },
        customActionIds: [] as string[],
        delay: 12,
        maxTokens: 2048,
        voiceEnabled: false,
      };
    },
    [createAgent.mutate],
  );

  const handleCreateAgent = useCallback(
    (overridePrompt?: string) => {
      if (!draft) return;

      setPhase('creating');
      const payload = buildCreatePayload(draft, overridePrompt);

      createAgent.mutate(payload as Parameters<typeof createAgent.mutate>[0], {
        onSuccess: () => {
          navigate('/automation/ai-agents');
          onClose();
        },
        onError: () => {
          toast.error('Erro ao criar o agente. Tente novamente.');
          setPhase('chatting');
        },
      });
    },
    [draft, buildCreatePayload, createAgent, navigate, onClose],
  );

  const handleStartCreate = useCallback(() => {
    if (!draft) return;

    const finalPrompt = draft.systemPrompt;
    if (!finalPrompt || finalPrompt.trim().length === 0) {
      toast.error('O assistente ainda não terminou de montar o prompt — continue a conversa um pouco mais.');
      return;
    }

    if (draft.migrationBlocks.length > 0) {
      setPhase('migrating');
    } else {
      setPhase('creating');
      handleCreateAgent(finalPrompt);
    }
  }, [draft, handleCreateAgent]);

  const handleClose = useCallback(() => {
    // Reset state on close
    setPhase('chatting');
    setMessages([makeMsg('assistant', INITIAL_GREETING)]);
    setDraft(null);
    setPendingComponent(undefined);
    onClose();
  }, [onClose]);

  // ─── Footer ─────────────────────────────────────

  const footer =
    phase === 'chatting' ? (
      <>
        <button
          onClick={handleClose}
          data-testid="agent-creation-cancel-button"
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#374151',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Cancelar
        </button>
        <button
          onClick={handleStartCreate}
          disabled={!draft}
          data-testid="agent-creation-submit-button"
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            border: 'none',
            background: draft ? '#8b5cf6' : '#e2e8f0',
            color: draft ? '#fff' : '#94a3b8',
            fontSize: '13px',
            fontWeight: 500,
            cursor: draft ? 'pointer' : 'not-allowed',
          }}
        >
          Criar Agente
        </button>
      </>
    ) : undefined;

  // ─── Render ─────────────────────────────────────

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="xl" title="Criar Agente" subtitle="Modo prático" footer={footer}>
      <div data-testid="agent-creation-drawer" style={{ height: '100%' }}>
      {phase === 'chatting' && (
        <div style={{ display: 'flex', height: '100%', minHeight: '500px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ChatPanel
              messages={messages}
              onSendMessage={handleSendMessage}
              isLoading={chatDraft.isPending}
              pendingComponent={pendingComponent}
              onActionConfirm={handleActionConfirm}
            />
          </div>
          {/* Preview: coluna fixa 290px, oculta no mobile (mockup ds-hide-mobile) */}
          <div className="hidden lg:block" style={{ width: 290, flex: 'none', backgroundColor: '#f9fafb' }}>
            <PreviewPanel draft={draft} migrationBlockCount={draft?.migrationBlocks?.length ?? 0} />
          </div>
        </div>
      )}

      {phase === 'migrating' && draft && (
        <PromptMigrationWizard
          systemPrompt={draft.systemPrompt}
          databaseIds={[
            ...new Set([
              ...(draft.databaseIds ?? []),
              ...draft.suggestedDatabases
                .filter((d) => d.existingDatabaseId)
                .map((d) => d.existingDatabaseId!),
            ]),
          ]}
          onComplete={(updatedPrompt) => {
            setDraft((prev) => (prev ? { ...prev, systemPrompt: updatedPrompt } : prev));
            handleCreateAgent(updatedPrompt);
          }}
          onCancel={() => setPhase('chatting')}
        />
      )}

      {phase === 'creating' && (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginBottom: '8px' }}>Criando seu agente...</div>
          <div style={{ fontSize: '13px', color: '#64748b' }}>Salvando configurações e preparando o agente</div>
        </div>
      )}
      </div>
    </Drawer>
  );
}

AgentCreationDrawer.displayName = 'AgentCreationDrawer';
